import { spineLoader } from '@play-co/timestep-spine';
import type { ThemeAsset } from 'src/game/theme/Theme';
import type { DreamDriftNodeStatus } from '../components/DreamDriftNode';
import type {
  DreamDriftHomePetAssets,
  DreamDriftHudButtonAssets,
  DreamDriftMapConfig,
  DreamDriftThemeProvider,
} from '../dreamDrift.theme';

/** Root folder containing the Asia Travel presentation assets. */
const themeFolder = 'assets/ui/dreamDrift/theme-asiatravel';

/** Folder containing Asia Travel's map slices, animations, and progression plates. */
const mapFolder = `${themeFolder}/map`;

/** Animated HUD icon and timer panel reused throughout an Asia Travel event. */
const hudButtonAssets: DreamDriftHudButtonAssets = {
  levelLabelLayout: { x: 125, y: 98, r: 0.2 },
  kind: 'spine',
  spine: {
    loadMethod: spineLoader,
    url: `${themeFolder}/hudButton/spine/hero.HUDicon`,
  },
  timer: `${themeFolder}/hudButton/timer.png`,
};

/** Bed shown beneath the sleeping pet during an Asia Travel run. */
const homePetAssets: DreamDriftHomePetAssets = {
  bed: `${themeFolder}/bed.png`,
};

/** Travel plate artwork associated with each regular node status. */
const NODE_IMAGES: Record<DreamDriftNodeStatus, string> = {
  cleared: 'node_cleared.png',
  current: 'node_current.png',
  open: 'node_open.png',
};

/** Checkpoint artwork shared by current and upcoming milestones. */
const CHECKPOINT_IMAGES: Record<DreamDriftNodeStatus, string> = {
  cleared: 'checkpoint_cleared.png',
  current: 'checkpoint_current.png',
  open: 'checkpoint_current.png',
};

/** Asia Travel map with animation and node positions measured from the supplied reference. */
const mapConfig = {
  // Repeat the bottom edge for two pixels so all four slices have equal height.
  dimensions: { width: 1080, height: 3452 },
  numSlices: 4,
  order: ['bg01.png', 'bg02.png', 'bg03.png', 'bg04.png'],
  chests: [
    { image: 'chest_bronze.png', x: 725, y: 2030, zIndex: 8 },
    { image: 'chest_silver.png', x: 635, y: 1235, zIndex: 5 },
    { image: 'chest_gold.png', x: 625, y: 355, zIndex: 0 },
  ],
  chests12: [
    { image: 'chest_bronze.png', x: 725, y: 2030, zIndex: 10 },
    { image: 'chest_silver.png', x: 635, y: 1235, zIndex: 6 },
    { image: 'chest_gold.png', x: 625, y: 355, zIndex: 0 },
  ],
  spine: {
    path: `${mapFolder}/spine/dreamdrift.map3`,
    animations: [
      // Keep the dragon beside the wall and the kite and plane in the open sky.
      { id: 'dragon_idle', x: 140, y: 1740, scale: 1 },
      { id: 'kite_idle', x: 970, y: 2420, scale: 1 },
      { id: 'plane_idle', x: 950, y: 805, scale: 1 },
    ],
  },
  nodes: [
    { x: 550, y: 2950 },
    { x: 683, y: 2743 },
    { x: 651, y: 2527 },
    { x: 619, y: 2078 },
    { x: 846, y: 1955 },
    { x: 705, y: 1695 },
    { x: 528, y: 1283 },
    { x: 735, y: 1163 },
    { x: 715, y: 893 },
    { x: 548, y: 553 },
  ],
  nodes12: [
    { x: 550, y: 2950 },
    { x: 683, y: 2743 },
    { x: 651, y: 2527 },
    { x: 619, y: 2078 },
    { x: 846, y: 1955 },
    { x: 858, y: 1800 },
    { x: 705, y: 1695 },
    { x: 528, y: 1283 },
    { x: 735, y: 1163 },
    { x: 816, y: 1028 },
    { x: 715, y: 893 },
    { x: 548, y: 553 },
  ],
} satisfies DreamDriftMapConfig;

/** Get every asset required to display the Asia Travel map and home pet. */
function getAssets(): ThemeAsset[] {
  // Preload the map artwork and shared Spine export before the popup opens.
  return [
    ...mapConfig.order.map(getMapImagePath),
    ...mapConfig.chests.map(({ image }) => getMapImagePath(image)),
    ...Object.values(NODE_IMAGES).map(getMapImagePath),
    ...[...new Set(Object.values(CHECKPOINT_IMAGES))].map(getMapImagePath),
    getMapImagePath('checkpoint_flag.png'),
    { url: mapConfig.spine.path, loadMethod: spineLoader },
    ...Object.values(homePetAssets),
  ];
}

/** Get the Asia Travel bed artwork used by the active home-pet presentation. */
function getHomePetAssets(): DreamDriftHomePetAssets {
  // Return the stable asset description for the persistent home view.
  return homePetAssets;
}

/** Get the animated Asia Travel HUD icon and its timer panel. */
function getHudButtonAssets(): DreamDriftHudButtonAssets {
  // Return the stable asset description so HUD updates can detect theme changes.
  return hudButtonAssets;
}

/** Get the square illustration displayed in the Asia Travel introduction. */
function getIntroImagePath(): string {
  // Resolve the introduction inside this theme's asset directory.
  return `${themeFolder}/intro.png`;
}

/** Get the travel background displayed behind Asia Travel puzzles. */
function getPuzzleBackground(): string {
  // Keep puzzle art aligned with the event's map and home presentation.
  return 'assets/minigame/dreamDrift/theme-asiatravel/bg.png';
}

/** Get the source dimensions and both Asia Travel progression paths. */
function getMapConfig(): DreamDriftMapConfig {
  // Return the map description consumed by the shared map component.
  return mapConfig;
}

/** Resolve a map image filename to its full Asia Travel asset path. */
function getMapImagePath(image: string): string {
  // Resolve plates, chests, and background slices within the map directory.
  return `${mapFolder}/${image}`;
}

/** Get the regular or checkpoint plate image for a progression status. */
function getNodeImage({
  status,
  isCheckpoint,
}: Parameters<DreamDriftThemeProvider['getNodeImage']>[0]): string {
  // Select the matching plate before resolving its full path.
  const images = isCheckpoint ? CHECKPOINT_IMAGES : NODE_IMAGES;
  return getMapImagePath(images[status]);
}

/**
 * Get the travel plate layout and upright brown numbering.
 * @param isCheckpoint Whether the plate marks a reward checkpoint.
 * @returns Plate bounds, number styling, and flag placement in source pixels.
 */
function getNodeStyle(
  isCheckpoint: boolean,
): ReturnType<DreamDriftThemeProvider['getNodeStyle']> {
  // Preserve the exported plate dimensions and leave room for the red location pin.
  return {
    width: isCheckpoint ? 204 : 167,
    height: isCheckpoint ? 159 : 135,
    label: {
      // Offset the number slightly left and up on the cream surface.
      x: isCheckpoint ? 96 : 77.5,
      y: isCheckpoint ? 47 : 39,
      color: '#84452B',
      r: 0,
    },
    flag: { x: 155, y: -25 },
  };
}

/** Asia Travel implementation of the shared Dream Drift presentation interface. */
export const dreamDriftThemeAsiaTravel: DreamDriftThemeProvider = {
  getAssets,
  getHomePetAssets,
  getHudButtonAssets,
  getIntroImagePath,
  getPuzzleBackground,
  getMapConfig,
  getMapImagePath,
  getNodeImage,
  getNodeStyle,
};
